import { FC } from "react";

import { AIActionSuggestion } from "../../../ai/components/AIActionSuggestion";

import { Trans, t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { FlagIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { NestedOutcomeGroupType, selectCurrentOrg } from "store/selectors";

export type ProgressOverviewBoxProps = {
  outcomeGroups: NestedOutcomeGroupType[];
  totalScore: number;
  totalAssessed: number;
};

export const ProgressOverviewBox: FC<ProgressOverviewBoxProps> = ({
  outcomeGroups,
  totalScore,
  totalAssessed,
}) => {
  const org = useAppSelector(selectCurrentOrg);

  const groupScores = outcomeGroups.map((group) => group.averageOutcomeScore!);
  const maxScore = Math.max(...groupScores);
  const minScore = Math.min(...groupScores);

  const strongGroup = outcomeGroups.find((group) => group.averageOutcomeScore === maxScore);
  const weakGroup = outcomeGroups.find((group) => group.averageOutcomeScore === minScore);

  const rubricLevel = org?.rubric?.find((level) => level.score === Math.floor(totalScore));

  if (!org || !rubricLevel) return null;

  if (totalAssessed === 0) {
    return (
      <div className="mb-4 flex flex-col gap-4 rounded-lg bg-black-tint-97 p-4">
        <IconText
          iconStart={<FlagIcon className="text-blue" />}
          text={t("progress_overview.no_outcomes_assessed.title")}
        />

        <div className="body-s">{t("progress_overview.no_outcomes_assessed.description")}</div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-xl border border-blue-tint-40 bg-blue-tint-97 p-6 text-base text-black-tint-20">
      <h2 className="text-blue-shade-20">{t("progress_overview.header")}</h2>

      <div className="pt-3">
        <Trans
          components={{ span: <span className="font-semibold" /> }}
          i18nKey={
            totalScore >= 3
              ? "progress_overview.overall_score_doing_well"
              : "progress_overview.overall_score"
          }
          t={t}
          values={{
            score: totalScore,
            total: org.rubric.length - 1,
            result: rubricLevel.name,
          }}
        />
      </div>

      <div className="pt-3">
        {t("progress_overview.summary", {
          totalAssessed,
          groupTotal: outcomeGroups.length,
          strongName: strongGroup?.title,
          strongScore: strongGroup?.averageOutcomeScore,
          weakName: weakGroup?.title,
          weakScore: weakGroup?.averageOutcomeScore,
        })}
      </div>

      <div className="mt-6 text-xs text-black-tint-40">{t("progress_overview.actions.title")}</div>

      <AIActionSuggestion
        fragment={t("progress_overview.actions.career.fragment")}
        fullText={t("progress_overview.actions.career.description")}
      />

      <AIActionSuggestion
        fragment={t("progress_overview.actions.study_plan.fragment")}
        fullText={t("progress_overview.actions.study_plan.description")}
      />

      <AIActionSuggestion
        fragment={t("progress_overview.actions.interview.fragment")}
        fullText={t("progress_overview.actions.interview.description")}
      />
    </div>
  );
};
